import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { passkeysHere, type WebConfig } from '../config.ts';
import type { Tenant } from '../tenant/tenant.ts';
import { Button } from '../components/Button.tsx';
import { ProviderButton } from '../components/ProviderButton.tsx';
import { PoweredBy } from '../components/States.tsx';
import { LegalLinks } from '../legal/LegalPage.tsx';
import { signInWithPasskey } from '../passkeys/api.ts';
import { clearAuthError, explain, readAuthError } from './authError.ts';

export interface SignInProps {
  readonly sb: SupabaseClient;
  readonly tenant: Tenant;
  readonly auth: WebConfig['auth'];
  /** Where the magic link returns to; defaults to this page (keeps `?tenant=` on preview hosts). */
  readonly redirectTo?: string;
  /** Why the sign-in this address brought back did not sign the person in (auth/authReturn.ts). */
  readonly notice?: string | null;
  /** Offer "Sign in with a passkey"; by default when this address is one of the build's passkey origins. */
  readonly passkeys?: boolean;
  readonly onHelp: () => void;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Sign-in (docs/reference/sso-and-registration.md). Invite-only: the magic link is requested with
 * `shouldCreateUser: false`, so an address that has not been invited cannot create an account here.
 */
export function SignIn({
  sb,
  tenant,
  auth,
  redirectTo,
  notice = null,
  passkeys,
  onHelp,
}: SignInProps): ReactElement {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyErr, setPasskeyErr] = useState<string | null>(null);
  const offerPasskey = passkeys ?? passkeysHere(auth);
  // A sign-in refused by Supabase comes back as ?error_description=… or #error_description=… (issue #6); a return
  // that could not sign in here (a link asked for in another browser) comes as `notice`.
  const [returned] = useState<string | null>(() => readAuthError() ?? notice);
  useEffect(() => {
    if (returned) clearAuthError();
  }, [returned]);

  async function sendLink(e: FormEvent): Promise<void> {
    e.preventDefault();
    const addr = email.trim();
    if (!EMAIL.test(addr)) {
      setError('Enter your email address, for example jane@example.com.');
      return;
    }
    setError(null);
    setState('sending');
    const { error: err } = await sb.auth.signInWithOtp({
      email: addr,
      options: {
        shouldCreateUser: false,
        emailRedirectTo:
          redirectTo ?? window.location.origin + window.location.pathname + window.location.search,
      },
    });
    if (err) {
      setState('idle');
      // Supabase answers "Signups not allowed for otp" for an address that has not been invited.
      setError(
        /signup|not allowed|not found/i.test(err.message)
          ? 'This email address has not been invited. Ask your administrator to send you an invitation.'
          : `Could not send the sign-in link: ${err.message}`,
      );
      return;
    }
    setState('sent');
  }

  // The sign-in email carries a code as well as the link (decision 890): typed here, in the browser that asked, it
  // signs in even when the email was read on another device, where the link cannot (PKCE, decision 801).
  async function signInWithCode(e: FormEvent): Promise<void> {
    e.preventDefault();
    const token = code.replace(/\s+/g, '');
    if (!/^\d{6,10}$/.test(token)) {
      setCodeErr('Type the code from the email: its digits only.');
      return;
    }
    setCodeErr(null);
    setCodeBusy(true);
    const { error: err } = await sb.auth.verifyOtp({ email: email.trim(), token, type: 'email' });
    setCodeBusy(false);
    if (err)
      setCodeErr(
        /expired|invalid/i.test(err.message)
          ? 'This code has expired or is wrong. Check the newest email, or ask for a new one.'
          : explain(err.message),
      );
  }

  async function passkey(): Promise<void> {
    setPasskeyErr(null);
    setPasskeyBusy(true);
    try {
      await signInWithPasskey(sb);
    } catch (e: unknown) {
      setPasskeyErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function oauth(provider: 'google' | 'azure' | 'linkedin_oidc'): Promise<void> {
    // Always show the provider's account chooser: Windows and the browser otherwise sign in silently with whichever
    // account they already hold (for example a work account), and the user cannot pick the invited one.
    const queryParams = { prompt: 'select_account' };
    await sb.auth.signInWithOAuth({
      provider,
      // Azure needs the email scope so Supabase can match the invited address.
      // LinkedIn (OpenID Connect: openid, profile, email by default) has no account chooser to ask for.
      options:
        provider === 'azure'
          ? { redirectTo: redirectTo ?? window.location.href, scopes: 'email', queryParams }
          : provider === 'linkedin_oidc'
            ? { redirectTo: redirectTo ?? window.location.href }
            : { redirectTo: redirectTo ?? window.location.href, queryParams },
    });
  }

  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="signin-title">
        {tenant.logo ? (
          <img className="signin__logo" src={tenant.logo} alt={tenant.name} />
        ) : (
          <span className="heading">{tenant.name}</span>
        )}
        <h1 id="signin-title" className="title">
          Sign in to {tenant.productTitle}
        </h1>
        {returned && (
          <p className="field__error body" role="alert">
            {returned}
          </p>
        )}
        {state === 'sent' ? (
          <div className="stack" role="status">
            <p className="body">
              Check your email. We sent a sign-in link to <strong>{email.trim()}</strong>. Press the link,
              then "Sign in to Teslyn" on the page it opens. The link and the code work once and expire after
              1 hour. Reading the email on another device? Type its code here instead.
            </p>
            <form className="stack" onSubmit={(e) => void signInWithCode(e)} noValidate>
              <label className="label muted" htmlFor="signin-code">
                Code from the email
              </label>
              <input
                id="signin-code"
                className="input data"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-invalid={codeErr ? true : undefined}
                aria-describedby={codeErr ? 'signin-code-error' : undefined}
              />
              {codeErr && (
                <p id="signin-code-error" className="field__error caption" role="alert">
                  {codeErr}
                </p>
              )}
              <Button type="submit" variant="secondary" busy={codeBusy}>
                {codeBusy ? 'Checking the code…' : 'Sign in with the code'}
              </Button>
            </form>
            <Button variant="quiet" onClick={() => setState('idle')}>
              Use a different email
            </Button>
          </div>
        ) : (
          <form className="stack" onSubmit={(e) => void sendLink(e)} noValidate>
            <label className="label muted" htmlFor="signin-email">
              Email
            </label>
            <input
              id="signin-email"
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'signin-error' : undefined}
            />
            {error && (
              <p id="signin-error" className="field__error caption" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" busy={state === 'sending'}>
              {state === 'sending' ? 'Sending sign-in link…' : 'Email me a sign-in link'}
            </Button>
          </form>
        )}
        <div className="stack">
          <ProviderButton
            provider="microsoft"
            disabled={!auth.microsoft}
            note={!auth.microsoft && <span className="caption"> · Available soon</span>}
            onClick={() => oauth('azure')}
          />
          <ProviderButton
            provider="google"
            disabled={!auth.google}
            note={!auth.google && <span className="caption"> · Available soon</span>}
            onClick={() => oauth('google')}
          />
          <ProviderButton
            provider="linkedin"
            disabled={!auth.linkedin}
            note={!auth.linkedin && <span className="caption"> · Available soon</span>}
            onClick={() => oauth('linkedin_oidc')}
          />
          {offerPasskey && (
            <>
              <Button variant="secondary" busy={passkeyBusy} onClick={() => void passkey()}>
                {passkeyBusy ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
              </Button>
              {passkeyErr && (
                <p className="field__error caption" role="alert">
                  {passkeyErr}
                </p>
              )}
            </>
          )}
        </div>
        <p className="caption muted">
          Access is by invitation only.{' '}
          <button type="button" className="link" onClick={onHelp}>
            Help with signing in
          </button>
        </p>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks search={window.location.search} />
      </footer>
    </div>
  );
}
